"use client";

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../ui/dialog";
import { Filter, X, Calendar } from "lucide-react";
import { Badge } from "../ui/badge";
import { useDispatch, useSelector } from "react-redux";
import { dateFilterData, intakeTableColumns } from "../../config/constant";
import {
  fetchDataForMultipleSelect,
  fetchFilterTotalApplications
} from "../../api/userService";
import {
  setAppetiteData,
  setCurrentPage,
  setActiveFilters,
  setTotalApplications
} from "../../store/ApplicationSlice";
import { FilterMultiSelect } from "./filter-multiselect";

export function ApplicationFilters ({
  setIsLoading,
  columnFilters,
  dateFilters,
  setDateFilters,
  setColumnFilters,
  handleUpdateData
}) {
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const uniqueValues = useSelector(state => state.intakeData.uniqueValues);
  const activeFilters = useSelector(state => state.intakeData.activeFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Store complete filter state
  const [previousState, setPreviousState] = useState({
    columnFilters: {},
    dateFilters: dateFilterData,
    activeFilters: []
  });

  // Save current state when opening dialog
  const handleOpenDialog = () => {
    setPreviousState({
      columnFilters,
      dateFilters,
      activeFilters
    });
    setShowAdvancedFilters(true);
  };

  const updateDataAndResetPage = async (filters, dateFilters) => {
    setIsLoading(true);
    dispatch(setCurrentPage(1));
    await handleUpdateData(1, filters, dateFilters);

    const response = await fetchDataForMultipleSelect(
      filters,
      dateFilters,
      accessToken,
      refreshToken
    );

    const totalCount = await fetchFilterTotalApplications(
      filters,
      dateFilters,
      accessToken,
      refreshToken
    );

    if (response) {
      dispatch(setAppetiteData(response));
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    setIsLoading(false);
  };

  const removeFilter = async filter => {
    const [label, rawValue] = filter.split(": ");
    const column = intakeTableColumns.find(col => col.label === label)?.key;

    let newColumnFilters = { ...columnFilters };
    let newDateFilters = { ...dateFilters };

    if (column === "date") {
      if (label === "Start Date") {
        newDateFilters.startDate = null;
      } else if (label === "End Date") {
        newDateFilters.endDate = null;
      }
      setDateFilters(newDateFilters);
    } else if (column) {
      delete newColumnFilters[column];
      setColumnFilters(newColumnFilters);
    }

    const newActiveFilters = activeFilters.filter(f => f !== filter);
    dispatch(setActiveFilters(newActiveFilters));

    // Update data with new filters and reset page
    await updateDataAndResetPage(newColumnFilters, newDateFilters);
  };

  const clearAllFilters = async () => {
    const emptyFilters = {};
    const defaultDateFilters = { ...dateFilterData };

    dispatch(setActiveFilters([]));
    setColumnFilters(emptyFilters);
    setDateFilters(defaultDateFilters);

    await updateDataAndResetPage(emptyFilters, defaultDateFilters);
  };

  const handleFilterChange = (column, selectedValue) => {
    let newColumnFilters = { ...columnFilters };
    const columnLabel =
      intakeTableColumns.find(col => col.key === column)?.label || column;

    // Handle null or undefined selectedValue
    if (!selectedValue || !Array.isArray(selectedValue)) {
      selectedValue = [];
    }

    // If no values selected, remove the filter entirely
    if (selectedValue.length === 0) {
      delete newColumnFilters[column];

      // Remove any active filters for this column
      const newActiveFilters = activeFilters.filter(
        f => !f.startsWith(`${columnLabel}:`)
      );
      dispatch(setActiveFilters(newActiveFilters));
    } else {
      // Update column filters with new selection
      newColumnFilters[column] = selectedValue;

      // Create new filter badge text
      const newFilter = `${columnLabel}: ${selectedValue.join(", ")}`;

      // Remove any existing filters for this column and add the new one
      const filtered = activeFilters.filter(
        f => !f.startsWith(`${columnLabel}:`)
      );
      dispatch(setActiveFilters([...filtered, newFilter]));
    }

    // Update column filters state
    setColumnFilters(newColumnFilters);
  };

  const handleApplyClick = async () => {
    setIsLoading(true);
    await updateDataAndResetPage(columnFilters, dateFilters);
    setShowAdvancedFilters(false);
  };

  const handleDialogClose = () => {
    // Restore all previous state when canceling
    setColumnFilters(previousState.columnFilters);
    setDateFilters(previousState.dateFilters);
    dispatch(setActiveFilters(previousState.activeFilters));
    setShowAdvancedFilters(false);
  };

  const sortedColumns = [
    ...intakeTableColumns.filter(col => col.key !== "date"),
    intakeTableColumns.find(col => col.key === "date")
  ];

  //function to update dateFilters state
  const updateFilter = (key, value) => {
    setDateFilters(prev => ({ ...prev, [key]: value }));

    const formattedValue = value ? value.toISOString().split("T")[0] : "";

    const labelMap = {
      startDate: "Start Date",
      endDate: "End Date"
    };

    const newFilter = `${labelMap[key]}: ${formattedValue}`;

    const filtered = activeFilters.filter(
      f => !f.startsWith(`${labelMap[key]}:`)
    );
    dispatch(
      setActiveFilters(formattedValue ? [...filtered, newFilter] : filtered)
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outlined"
          className="flex items-center gap-2"
          onClick={handleOpenDialog}
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
        </Button>

        <Dialog
          open={showAdvancedFilters}
          onOpenChange={open => {
            if (!open) handleDialogClose();
            else setShowAdvancedFilters(open);
          }}
          modal={true}
        >
          <DialogContent className="sm:max-w-[850px] bg-white rounded-lg shadow-lg">
            <DialogHeader className="pb-4 border-b">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <DialogTitle className="text-xl font-semibold">
                  Advanced Filters
                </DialogTitle>
              </div>
              <DialogDescription className="text-gray-500 mt-1">
                Customize your application view with advanced filtering options.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-4 py-4">
              {sortedColumns.map(({ key, label }) => {
                const options = uniqueValues?.[key] || [];

                // If key is 'date', render the date range UI
                if (key === "date") {
                  const renderDateInput = (id, label) => {
                    const dateValue = dateFilters[id];
                    const formattedDate = dateValue
                      ? dateValue.toISOString().split("T")[0]
                      : "";

                    return (
                      <Input
                        key={id}
                        type="date"
                        className="flex-1"
                        value={formattedDate}
                        onChange={e => {
                          const newValue = e.target.value
                            ? new Date(e.target.value)
                            : null;
                          updateFilter(id, newValue);
                        }}
                        aria-label={label}
                      />
                    );
                  };

                  return (
                    <div
                      key={key}
                      className="space-y-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <label className="font-medium text-gray-700">
                          Date Range
                        </label>
                      </div>
                      <div className="flex flex-col gap-3">
                        <div className="w-full">
                          <label className="text-xs text-gray-500 mb-1 block">
                            Start Date
                          </label>
                          {renderDateInput("startDate", "Start Date")}
                        </div>
                        <div className="w-full">
                          <label className="text-xs text-gray-500 mb-1 block">
                            End Date
                          </label>
                          {renderDateInput("endDate", "End Date")}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Skip if no options
                if (!options.length) return null;

                // Render Select for all other keys
                return (
                  <div
                    key={key}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FilterMultiSelect
                      keyName={key}
                      label={label}
                      value={columnFilters[key] || []}
                      onValueChange={value => {
                        handleFilterChange(key, value);
                      }}
                      options={options.filter(Boolean)}
                      openDropdownKey={openDropdownKey}
                      setOpenDropdownKey={setOpenDropdownKey}
                    />
                  </div>
                );
              })}
            </div>

            <DialogFooter className="border-t pt-4">
              <Button variant="outlined" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                className="bg-[#FF5533] hover:bg-[#E04A2F] text-white"
                onClick={handleApplyClick}
              >
                Apply Filters
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center flex-wrap gap-2">
        <div
          className="flex items-center gap-2 overflow-x-auto whitespace-nowrap"
          style={{ width: "500px" }}
        >
          {activeFilters.map((filter, idx) => (
            <Badge
              key={`${filter}-${idx}`}
              variant="outlined"
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 flex-shrink-0 rounded-full transition-all duration-200 ease-in-out group relative"
            >
              {filter}
              <X
                role="button"
                aria-label={`Remove filter ${filter}`}
                className="h-3.5 w-3.5 ml-1.5 cursor-pointer opacity-50 group-hover:opacity-100 hover:text-red-500 transition-all"
                onClick={() => removeFilter(filter)}
              />
            </Badge>
          ))}
        </div>
        {activeFilters.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-sm h-7 text-gray-500 hover:text-gray-700 flex-shrink-0"
            onClick={clearAllFilters}
          >
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
}
